import { useEffect, useState } from "react";

import { useCreateRisk, useEditRisk, useFetchRisk } from "../mutation";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";
import { usePhase } from "../../projectOverview/hooks/usePhase";
import { useSearchProjects } from "../../projects/mutations";
import dayjs from "dayjs";

export const useRisk = ({ editStatus, riskId, projectId, refetch }: { editStatus: boolean, riskId: string | null, projectId: string | undefined, refetch: () => void }) => {

    const createRisk = useCreateRisk();
    const editRisk = useEditRisk();

    const { riskData, isRiskPending, refetch: refetchRisk } = useFetchRisk({ riskId: riskId });

    const phaseData = usePhase();
    const searchProjects = useSearchProjects();

    const [overviewData, setOverviewData] = useState<{
        riskTitle: null | string
        riskDescription: null | string
        riskCategory: null | string
        riskStatus: null | string
        riskReviewDate: null | string
        type: 'Project' | 'Portfolio' | 'Programme',
        phase: null | string,
        project: null | AutocompleteOption,
        portfolio: null | AutocompleteOption,
        programme: null | AutocompleteOption,
    }>({
        riskTitle: null,
        riskDescription: null,
        riskCategory: null,
        riskStatus: null,
        riskReviewDate: null,
        type: 'Project',
        phase: null,
        project: null,
        portfolio: null,
        programme: null,
    });

    const onSaveRisk = async () => {
        const payload = {
            title: overviewData.riskTitle,
            risk_description: overviewData.riskDescription,
            category: overviewData.riskCategory,
            status: overviewData.riskStatus,
            review_date: overviewData.riskReviewDate ? dayjs(overviewData.riskReviewDate).utc().toISOString() : null,
            project_id: overviewData.project?.id,
            project_tree_id: overviewData.phase,
            programme_id: overviewData.programme?.id,
            portfolio_id: overviewData.portfolio?.id,
            type: overviewData.type,
        };

        if (editStatus && riskId) {
            await editRisk.mutateAsync({ riskId: riskId.toString(), data: payload });
        } else {
            await createRisk.mutateAsync({ data: payload });
        }

        refetch();
        refetchRisk();
    };

    const fetchProjectDetails = async () => {
        const params: Record<string, any> = {
            project_id: projectId
        };
        const response = await searchProjects.mutateAsync({ params });
        setOverviewData({
            ...overviewData,
            project: {
                id: response.data[0].id,
                label: response.data[0].title
            }
        });
    };

    useEffect(() => {
        if (projectId && !riskId)
            fetchProjectDetails();
    }, [projectId]);

    useEffect(() => {
        if (riskId) {
            setOverviewData({
                riskTitle: riskData?.risk_title,
                riskDescription: riskData?.risk_description,
                riskCategory: riskData?.risk_category,
                riskStatus: riskData?.risk_status,
                riskReviewDate: riskData?.review_date,
                type: riskData?.type,
                phase: riskData?.project_tree_id,
                project: { id: riskData?.project_id, label: riskData?.project_title },
                portfolio: { id: riskData?.portfolio_id, label: riskData?.portfolio_title },
                programme: { id: riskData?.programme_id, label: riskData?.programme_title }
            });
        }
    }, [riskId, riskData, isRiskPending]);

    useEffect(() => {
        if (overviewData.project?.id && !Array.isArray(overviewData.project))
            phaseData.mutateAsync(overviewData.project.id as string);
    }, [overviewData.project]);

    return {
        onSaveRisk,
        overviewData, setOverviewData,
        isRiskPending,
        isSaving: createRisk.isPending || editRisk.isPending,
        phaseOptions: phaseData.data?.data || [],
        isPhasePending: phaseData.isPending,
    };

};